import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { AudioRecorder } from '@/shared/lib/audio-recorder';
import { taskQueue } from '@/shared/lib/task-queue';

import { useStartRecordingOptions } from '../api/audio-recording.mutations';
import { recordingWebsocket } from '../api/audio-recording.websocket';
import { createChunkStampAllocator } from '../lib/chunk-stamp-allocator';
import { useMicStore } from './mic-store';
import { useRecordingStore } from './recording-store';
import { useDisplayMediaControls } from './use-display-media-controls';

const TIME_SLICE = 1000;

export function useRecordingControls() {
    const { recordId, audioRecorder, length, _start, _pause, _resume, _stop, _reset, _failStart } =
        useRecordingStore(
            useShallow((state) => ({
                recordId: state.recordId,
                audioRecorder: state.audioRecorder,
                length: state.length,
                _start: state._start,
                _pause: state._pause,
                _resume: state._resume,
                _stop: state._stop,
                _reset: state._reset,
                _failStart: state._failStart,
            })),
        );

    const micStream = useMicStore((state) => state.currentStream);

    const { capture, clear: clearDisplayMedia } = useDisplayMediaControls();

    const { mutateAsync: startRecording } = useMutation(
        useStartRecordingOptions({
            onError: () => {
                toast.error('Failed to start recording');
                taskQueue.clear();
                _failStart();
                clearDisplayMedia();
            },
        }),
    );

    const start = useCallback(
        async (recordId: string, captureDisplayMedia: boolean) => {
            if (!micStream) {
                return false;
            }

            let audioRecorder: AudioRecorder | undefined;

            // Nothing below touches the store until _start() commits, so every failure path only
            // has to dispose its own recorder — never _reset(), which would tear down a recording
            // this call never owned.
            try {
                audioRecorder = new AudioRecorder(TIME_SLICE);
                const allocateChunkStamp = createChunkStampAllocator();

                if (captureDisplayMedia) {
                    const displayMediaResult = await capture();

                    if (!displayMediaResult.success && displayMediaResult.accessDenied) {
                        audioRecorder.dispose();
                        return false;
                    }

                    if (displayMediaResult.stream) {
                        await audioRecorder.connectStream(
                            'displayMedia',
                            displayMediaResult.stream,
                        );
                    } else {
                        toast.warning('Tab audio not shared', {
                            description:
                                'We could not detect audio from the shared tab. Please re-share and enable tab audio.',
                        });
                    }
                }

                await audioRecorder.connectStream('mic', micStream);

                audioRecorder.onDataAvailable((data: Blob) => {
                    // Stamped here, not in the task: a blocked queue releases blobs back to back,
                    // and a drain-time Date.now() gives them the same DynamoDB sort key.
                    const timestamp = allocateChunkStamp(data.size);

                    taskQueue.add(() => recordingWebsocket.chunk(recordId, data, timestamp));
                });

                await audioRecorder.start();

                if (!_start(recordId, audioRecorder)) {
                    audioRecorder.dispose();
                    return false;
                }

                taskQueue.add(() => startRecording(recordId));

                return true;
            } catch (error) {
                console.error('Error starting recording:', error);
                audioRecorder?.dispose();
                return false;
            }
        },
        [_start, micStream, capture, startRecording],
    );

    const pause = useCallback(() => {
        if (_pause()) {
            audioRecorder?.pause();
        }
    }, [_pause, audioRecorder]);

    const resume = useCallback(async () => {
        if (!_resume()) {
            return;
        }

        if (length === 'too-long' || !micStream) {
            _pause();
            return;
        }

        try {
            // start() alone still throws — this is a deliberate user action, not an effect racing
            // teardown, so a silent no-op here would leave the UI claiming to record.
            await audioRecorder?.start();
        } catch (error) {
            console.error('Error resuming recording:', error);
            toast.error('Failed to resume recording');
            _pause();
        }
    }, [length, micStream, _resume, _pause, audioRecorder]);

    const stop = useCallback(() => {
        if (_stop()) {
            audioRecorder?.pause();
        }
    }, [_stop, audioRecorder]);

    const finish = useCallback(async () => {
        if (recordId) {
            // Only the tail chunk must precede the finish signal; teardown is unrelated cleanup
            // and runs unawaited in the background where a hang harms nothing.
            await audioRecorder?.flush();
            taskQueue.add(() => recordingWebsocket.finish(recordId));
            _reset();
            clearDisplayMedia();
        }
    }, [recordId, audioRecorder, _reset, clearDisplayMedia]);

    const abort = useCallback(async () => {
        if (recordId) {
            taskQueue.add(() => recordingWebsocket.abort(recordId));
            _reset();
            clearDisplayMedia();
        }
    }, [recordId, _reset, clearDisplayMedia]);

    return {
        start,
        pause,
        resume,
        stop,
        finish,
        abort,
    };
}
